import { useState } from 'react'

interface UseCloseExecutionCycleDialogParams {
  closeExecutionCycle: (
    generateInvoices: boolean,
    startDate?: string,
    endDate?: string,
    reInvoiceType?: number,
    groupingType?: number
  ) => void
  /** Comprobación previa a abrir el diálogo: si resuelve false, el diálogo no se abre */
  canOpen?: () => Promise<boolean>
}

interface UseCloseExecutionCycleDialogReturn {
  isOpen: boolean
  openDialog: () => Promise<void>
  closeDialog: () => void
  onCloseWithInvoices: (
    startDate: string,
    endDate: string,
    reInvoiceType?: number,
    groupingType?: number
  ) => void
  onCloseWithoutInvoices: () => void
}

/**
 * Hook para manejar el estado y las acciones del diálogo de cierre de ciclo de ejecución
 *
 * @param closeExecutionCycle - Función que cierra el ciclo de ejecución (del hook useExecutionCycleActions)
 * @returns Un objeto con:
 * - isOpen: Estado del diálogo (abierto/cerrado)
 * - openDialog: Función para abrir el diálogo
 * - closeDialog: Función para cerrar el diálogo
 * - onCloseWithInvoices: Handler para cerrar con generación de facturas
 * - onCloseWithoutInvoices: Handler para cerrar sin generación de facturas
 */
export const useCloseExecutionCycleDialog = ({
  closeExecutionCycle,
  canOpen
}: UseCloseExecutionCycleDialogParams): UseCloseExecutionCycleDialogReturn => {
  const [isOpen, setIsOpen] = useState(false)

  const openDialog = async () => {
    if (canOpen && !(await canOpen())) return
    setIsOpen(true)
  }
  const closeDialog = () => setIsOpen(false)

  const onCloseWithInvoices = (
    startDate: string,
    endDate: string,
    reInvoiceType?: number,
    groupingType?: number
  ) => {
    const generateInvoices = true
    closeExecutionCycle(generateInvoices, startDate, endDate, reInvoiceType, groupingType)
    closeDialog()
  }

  const onCloseWithoutInvoices = () => {
    const generateInvoices = false
    closeExecutionCycle(generateInvoices)
    closeDialog()
  }

  return {
    isOpen,
    openDialog,
    closeDialog,
    onCloseWithInvoices,
    onCloseWithoutInvoices
  }
}
